import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Printer, TrendingUp, Receipt, IndianRupee, ShoppingCart, Calendar } from "lucide-react";

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "year", label: "This Year" },
  { key: "all", label: "All Time" },
];

function fmt(n) { return "₹" + (Number(n) || 0).toFixed(2); }
function fmtDate(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso; } }

export default function AdminRevenue() {
  const [period, setPeriod] = useState("month");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [report, setReport] = useState(null);
  const [settings, setSettings] = useState({ clinic_name: "Immortal Vedas", clinic_address: "", clinic_gstin: "", gst_rate: 0.05 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = from && to ? { from_date: from, to_date: to } : { period };
      const { data } = await api.get("/admin/revenue", { params });
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, [period]); // eslint-disable-line
  useEffect(() => { api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {}); }, []);

  const kpis = useMemo(() => ([
    { label: "Orders", val: report?.total_orders || 0, icon: ShoppingCart, color: "bg-emerald-50 text-primary" },
    { label: "Net Sales", val: fmt(report?.gross_revenue), icon: TrendingUp, color: "bg-lime-50 text-primary" },
    { label: "GST Collected", val: fmt(report?.tax_collected), icon: Receipt, color: "bg-amber-50 text-secondary" },
    { label: "Grand Total", val: fmt(report?.grand_total), icon: IndianRupee, color: "bg-rose-50 text-primary" },
  ]), [report]);

  const doPrint = () => window.print();

  return (
    <div>
      {/* Non-print controls */}
      <div className="print:hidden">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {PERIODS.map((p) => (
              <button key={p.key} onClick={() => { setFrom(""); setTo(""); setPeriod(p.key); }} data-testid={`period-${p.key}`}
                className={`px-4 py-2 rounded-full text-sm ${period === p.key && !from ? "bg-primary text-primary-foreground" : "border border-border hover:border-primary/40"}`}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider">From</Label>
              <Input type="date" value={from} onChange={(e)=>setFrom(e.target.value)} className="h-9 bg-transparent" data-testid="rev-from"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider">To</Label>
              <Input type="date" value={to} onChange={(e)=>setTo(e.target.value)} className="h-9 bg-transparent" data-testid="rev-to"/>
            </div>
            <Button onClick={load} disabled={busy || !from || !to} variant="outline" className="btn-pill h-9" data-testid="rev-apply">
              Apply
            </Button>
            <Button onClick={doPrint} className="btn-pill bg-primary text-primary-foreground h-9" data-testid="rev-print">
              <Printer size={14} className="mr-1.5"/> Print
            </Button>
          </div>
        </div>
      </div>

      {/* Printable area */}
      <div id="print-area" className="bg-card border border-border rounded-2xl p-6 lg:p-10">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h2 className="font-display text-3xl">{settings.clinic_name || "Immortal Vedas"}</h2>
            {settings.clinic_address && <p className="text-sm text-muted-foreground">{settings.clinic_address}</p>}
            {settings.clinic_gstin && <p className="text-xs text-muted-foreground mt-1">GSTIN: {settings.clinic_gstin}</p>}
          </div>
          <div className="text-right">
            <div className="overline">Revenue Report</div>
            <div className="text-sm text-muted-foreground">
              {report?.from ? new Date(report.from).toLocaleDateString() : "—"} → {report?.to ? new Date(report.to).toLocaleDateString() : "—"}
            </div>
            <div className="text-xs text-muted-foreground">Generated {new Date().toLocaleString()}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {kpis.map((k) => (
            <Card key={k.label} className="border-border">
              <CardContent className="p-5">
                <div className={`w-10 h-10 rounded-lg ${k.color} flex items-center justify-center mb-3`}><k.icon size={16}/></div>
                <div className="font-display text-2xl">{k.val}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k.label}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <h3 className="font-display text-xl mb-3 flex items-center gap-2"><Calendar size={16}/> Paid Orders</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="py-2 pr-3">Order</th>
                <th className="py-2 pr-3">Date</th>
                <th className="py-2 pr-3">Customer</th>
                <th className="py-2 pr-3">Phone</th>
                <th className="py-2 pr-3 text-right">Subtotal</th>
                <th className="py-2 pr-3 text-right">GST</th>
                <th className="py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {(report?.orders || []).map((o) => (
                <tr key={o.id} className="border-b border-border/60" data-testid={`rev-row-${o.id}`}>
                  <td className="py-2 pr-3 font-mono text-xs">#{o.id.slice(0,8)}</td>
                  <td className="py-2 pr-3">{fmtDate(o.date)}</td>
                  <td className="py-2 pr-3">{o.customer}</td>
                  <td className="py-2 pr-3">{o.phone}</td>
                  <td className="py-2 pr-3 text-right">{fmt(o.subtotal)}</td>
                  <td className="py-2 pr-3 text-right">{fmt(o.tax_amount)}</td>
                  <td className="py-2 text-right font-medium">{fmt(o.total)}</td>
                </tr>
              ))}
              {(!report?.orders || report.orders.length === 0) && (
                <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">No paid orders in this period.</td></tr>
              )}
            </tbody>
            {report?.orders?.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-border font-medium">
                  <td colSpan={4} className="py-3 pr-3 text-right">Totals</td>
                  <td className="py-3 pr-3 text-right">{fmt(report.gross_revenue)}</td>
                  <td className="py-3 pr-3 text-right">{fmt(report.tax_collected)}</td>
                  <td className="py-3 text-right font-display text-xl">{fmt(report.grand_total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <div className="mt-8 text-xs text-muted-foreground print:block">
          <p>This report is auto-generated from the Immortal Vedas admin panel. GST is calculated at {((report?.orders?.[0]?.tax_amount && report?.orders?.[0]?.subtotal) ? (report.orders[0].tax_amount / report.orders[0].subtotal * 100).toFixed(1) : (settings.gst_rate*100).toFixed(1))}% on the net sale value. Preserve for tax filing.</p>
        </div>
      </div>

      <style>{`
        @media print {
          body * { visibility: hidden; }
          #print-area, #print-area * { visibility: visible; }
          #print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
          nav, header, aside, .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}
